import { useState, useMemo } from "react";
import Papa from "papaparse";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Upload, FileSpreadsheet, CheckCircle2, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// App fields the importer can populate. required flag on customer_name.
const APP_FIELDS = [
  { key: "customer_name", label: "Customer name", required: true, guess: ["customer", "site contact", "contact name", "name", "client"] },
  { key: "contact_person", label: "Contact person", guess: ["contact", "site contact"] },
  { key: "email", label: "Email", guess: ["email", "e-mail"] },
  { key: "phone", label: "Phone", guess: ["phone", "mobile", "tel"] },
  { key: "billing_address", label: "Billing address", guess: ["billing", "invoice address"] },
  { key: "site_name", label: "Site name", guess: ["site name", "property"] },
  { key: "site_address", label: "Site address", guess: ["site address", "address", "site"] },
  { key: "manufacturer", label: "Manufacturer", guess: ["manufacturer", "make", "brand"] },
  { key: "model", label: "Model", guess: ["model", "indoor model"] },
  { key: "serial_number", label: "Serial number", guess: ["serial", "indoor serial"] },
  { key: "indoor_unit", label: "Indoor unit", guess: ["indoor model", "indoor unit", "indoor"] },
  { key: "outdoor_unit", label: "Outdoor unit", guess: ["outdoor model", "outdoor unit", "outdoor"] },
  { key: "refrigerant_type", label: "Refrigerant", guess: ["refrigerant", "gas"] },
  { key: "installation_date", label: "Installation date", guess: ["install", "commission"] },
  { key: "last_service_date", label: "Last service date", guess: ["last service", "date", "service date"] },
  { key: "notes", label: "Notes", guess: ["notes", "recommendation", "comment"] },
];

const TEMPLATE_HEADERS = APP_FIELDS.map((f) => f.key);

function autoGuess(header, columns) {
  const cols = columns.map((c) => c.toLowerCase());
  for (const g of header.guess) {
    const i = cols.findIndex((c) => c.includes(g));
    if (i !== -1) return columns[i];
  }
  return "";
}

export default function ImportData() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const cols = res.meta.fields || [];
        setColumns(cols);
        setRows(res.data);
        const initial = {};
        APP_FIELDS.forEach((f) => {
          initial[f.key] = autoGuess(f, cols);
        });
        setMapping(initial);
        toast.success(`Loaded ${res.data.length} rows`);
      },
      error: () => toast.error("Could not parse file — please upload a .csv"),
    });
    e.target.value = "";
  };

  const mappedRows = useMemo(() => {
    return rows.map((r) => {
      const out = {};
      APP_FIELDS.forEach((f) => {
        const col = mapping[f.key];
        out[f.key] = col ? String(r[col] ?? "").trim() : "";
      });
      return out;
    });
  }, [rows, mapping]);

  const validRows = mappedRows.filter((r) => r.customer_name);

  const downloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sterio_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (validRows.length === 0) return toast.error("Map the 'Customer name' column first");
    setImporting(true);
    try {
      const { data } = await api.post("/import/customers", { rows: mappedRows });
      setResult(data);
      toast.success(`Imported ${data.created_customers} customers`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Import customers</h1>
        <p className="text-sm text-slate-500">Bulk-create customers, sites and heat pumps from a CSV.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <label className="flex h-11 cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white" data-testid="upload-csv-label">
          <Upload className="h-4 w-4" /> Upload CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="upload-csv-input" />
        </label>
        <Button variant="outline" className="h-11" onClick={downloadTemplate} data-testid="download-template">
          <Download className="mr-1.5 h-4 w-4" /> Template
        </Button>
      </div>

      {columns.length > 0 && (
        <>
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary" />
              <span className="text-sm font-bold text-slate-900">Map your columns</span>
              <Badge variant="outline" className="ml-auto text-[10px]">{rows.length} rows</Badge>
            </div>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {APP_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <Label className="w-36 shrink-0 text-xs">
                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                  </Label>
                  <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
                  <select
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                    data-testid={`map-${f.key}`}
                    className="h-9 w-full rounded-md border border-input bg-white px-2 text-xs"
                  >
                    <option value="">— skip —</option>
                    {columns.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </Card>

          {/* preview */}
          <Card className="p-4">
            <p className="mb-2 text-sm font-bold text-slate-900">Preview (first 5)</p>
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-slate-400">
                    <th className="py-1 pr-3">Customer</th>
                    <th className="py-1 pr-3">Site address</th>
                    <th className="py-1 pr-3">Model</th>
                    <th className="py-1 pr-3">Serial</th>
                  </tr>
                </thead>
                <tbody>
                  {mappedRows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`preview-row-${i}`}>
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{r.customer_name || <span className="text-red-400">—</span>}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{r.site_address}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{r.model || r.indoor_unit}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{r.serial_number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              {validRows.length} of {rows.length} rows have a customer name and will be imported. Rows without a customer name are skipped.
            </p>
            <Button className="mt-3 h-11 w-full" onClick={runImport} disabled={importing || validRows.length === 0} data-testid="run-import">
              Import {validRows.length} rows
            </Button>
          </Card>
        </>
      )}

      {result && (
        <Card className="p-4" data-testid="import-result">
          <div className="mb-2 flex items-center gap-2 text-primary">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-bold">Import complete</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <Stat label="Customers" value={result.created_customers} />
            <Stat label="Sites" value={result.created_sites} />
            <Stat label="Heat pumps" value={result.created_pumps} />
            <Stat label="Skipped" value={result.skipped} />
          </div>
          {result.errors?.length > 0 && (
            <p className="mt-3 text-xs text-red-600">{result.errors.length} row error(s): {result.errors.slice(0, 3).map((e) => `row ${e.row}`).join(", ")}…</p>
          )}
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-center">
      <div className="font-heading text-xl font-extrabold text-slate-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}
